import { ReactNode, FC, useContext } from "react";
import { useHistory } from "react-router-dom";

import { FeatureFlagContext } from "@/common/contexts/FeatureFlagContext";


export interface OnFeatureFlagProps {
    /**
     * The value(s) to check for in feature flags. This can be a single string or an array of strings.
     * Note: If both flag and flags are provided, flags takes priority
     */
    flag: string | string[];

    /**
     * The url to redirect to if the flag is not present
     * If it is not provided, it will not redirect
     */
    redirect?: string;

    /**
     * Node to render if the flag is not present
     * Limited to a single node to discourage having too much code in the tag itself
     */
    renderOnDisabled?: ReactNode;

    /**
     * The child elements passed to this component
     */
    children: ReactNode | ReactNode[];
}

export const OnFeatureFlag: FC<OnFeatureFlagProps> = ({ flag, children, redirect, renderOnDisabled }) => {
    const featureFlags = useContext(FeatureFlagContext);
    const history = useHistory();
    const loaded = featureFlags.hasFetchedFlags();

    const shouldRender = typeof flag === "string" ? featureFlags.hasFeatureFlag(flag) : featureFlags.hasFeatureFlags(flag);

    // The feature flags are refetched when a user is redirected to a page outside of react-router
    // if we don't wait until feature flags are loaded, the user will always be redirected.
    if (!shouldRender && loaded && redirect) history.push(redirect);

    return (
        <>
            {shouldRender && children}
            {!shouldRender && renderOnDisabled}
        </>
    );
};
